import React from "react";
import { Link } from "react-router-dom";
import classNames from "../../utils/classNames";

// Single button primitive for the whole app.
// - variant: "primary" | "secondary" | "link"  (secondary is dark-mode aware)
// - size:    "sm" | "md" | "lg"  (density)
// - icon:    a Heroicon component (not JSX); pair with iconPosition
// - icon-only: pass `icon` with no children; an aria-label is then required
// - href:    when set, renders a router <Link>; otherwise a <button>
const SIZES = {
  sm: { text: "px-3 py-1.5 text-xs gap-x-1.5", iconOnly: "p-1.5", icon: "h-4 w-4" },
  md: { text: "px-4 py-2 text-sm gap-x-2", iconOnly: "p-2", icon: "h-5 w-5" },
  lg: { text: "px-6 py-2.5 text-sm gap-x-2", iconOnly: "p-2.5", icon: "h-5 w-5" },
};

// Disabled buttons drop their color for one shared gray: a flat fill (no
// shadow) and low-contrast text, so they read as unavailable rather than as a
// faded version of the enabled button.
const DISABLED = "bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500 cursor-not-allowed";

const VARIANTS = {
  primary: {
    enabled:
      "shadow-sm bg-brand-primary text-white hover:bg-brand-200 focus-visible:outline-brand-100",
    disabled: DISABLED,
  },
  secondary: {
    enabled:
      "shadow-sm bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600 focus-visible:outline-brand-100",
    disabled: DISABLED,
  },
  link: {
    enabled:
      "text-brand-primary hover:text-brand-200 focus-visible:outline-brand-primary",
    // Links have no fill, so disabled is just the low-contrast text.
    disabled: "text-gray-400 dark:text-gray-500 cursor-not-allowed",
  },
};

// Icon-only buttons share one subtle, dark-mode-aware treatment regardless of
// variant (matches the add/remove/delete affordances they replace).
const ICON_ONLY = {
  enabled:
    "text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 focus-visible:outline-brand-100",
  disabled: "text-gray-300 dark:text-gray-600 cursor-not-allowed",
};

function Button({
  children,
  variant = "primary",
  size = "md",
  icon: Icon,
  iconPosition = "left",
  href,
  type = "button",
  disabled = false,
  fullWidth = false,
  className = "",
  onClick,
  ...rest
}) {
  const sizes = SIZES[size] ?? SIZES.md;
  const iconOnly = Icon && !children;

  const classes = classNames(
    "inline-flex items-center justify-center rounded-md font-semibold transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
    fullWidth ? "w-full" : "",
    iconOnly ? sizes.iconOnly : sizes.text,
    iconOnly
      ? disabled
        ? ICON_ONLY.disabled
        : ICON_ONLY.enabled
      : disabled
        ? VARIANTS[variant]?.disabled
        : VARIANTS[variant]?.enabled,
    className,
  );

  const content = (
    <>
      {Icon && iconPosition === "left" && (
        <Icon className={sizes.icon} aria-hidden="true" />
      )}
      {children}
      {Icon && iconPosition === "right" && (
        <Icon className={sizes.icon} aria-hidden="true" />
      )}
    </>
  );

  if (href) {
    return (
      <Link
        to={disabled ? "#" : href}
        className={classes}
        aria-disabled={disabled || undefined}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          onClick?.(e);
        }}
        {...rest}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      className={classes}
      {...rest}
    >
      {content}
    </button>
  );
}

export { Button };
